import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Single shared axios instance — cookies always included.
export const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await apiClient.get("/auth/me");
      setUser(res.data);
    } catch (_e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (
      typeof window !== "undefined" &&
      window.location.hash?.includes("session_id=")
    ) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch (err) {
      // best-effort logout — clearing client state regardless, but log the failure
      // so the user (and ops) know the server-side cookie may still be live.
      console.warn("AuthContext.logout: server logout failed", err);
    }
    setUser(null);
  }, []);

  // Permission helper — `permissions` array is shipped on /auth/me.
  // Pre-existing tokens issued before the multi-tier rollout may not contain
  // the array; in that case we fall back to the legacy role string.
  const can = useCallback(
    (perm) => {
      if (!user) return false;
      if (Array.isArray(user.permissions)) return user.permissions.includes(perm);
      // Legacy fallback: super_admin & admin had everything.
      return user.role === "super_admin" || user.role === "admin";
    },
    [user]
  );

  // Memoise the context value so consumers don't re-render on every parent render.
  const value = useMemo(
    () => ({ user, setUser, loading, checkAuth, logout, can }),
    [user, loading, checkAuth, logout, can]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
